import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "../../assets/styles/home.module.css";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { Typography } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { axiosInstance } from "../../utils/networking";

const theme = createTheme({
  palette: { secondary: { main: "#9e1f63", contrastText: "#fff" } },
});

// Public page reached from the invite / reset email link. Validates the
// setup token, then lets the user choose a password. Listed in the public
// route allowlist (utils/auth.tsx + App.tsx) so it bypasses auth validation.
const SetPassword = () => {
  const [params] = useSearchParams();
  const uid = params.get("uid") || "";
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [tokenState, setTokenState] = useState<
    "checking" | "valid" | "invalid"
  >("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!uid || !token) {
      setTokenState("invalid");
      return;
    }
    axiosInstance
      .get(`/auth/setup-token/validate`, { params: { uid, token } })
      .then((res) => setTokenState(res.data.valid ? "valid" : "invalid"))
      .catch(() => setTokenState("invalid"));
  }, [uid, token]);

  const requestNewLink = () => {
    axiosInstance
      .post(`/auth/request-setup`, { userId: uid })
      .finally(() =>
        setMessage(
          "If the account exists, a new link has been sent to your email."
        )
      );
  };

  const handleSubmit = () => {
    setMessage("");
    if (password.length < 8) {
      setMessage("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setMessage("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    axiosInstance
      .post(`/auth/set-password`, { uid, token, password })
      .then(() => {
        setDone(true);
        setTimeout(() => navigate("/"), 2500);
      })
      .catch((err) => {
        setSubmitting(false);
        if (err?.response?.data?.reason === "invalid_or_expired") {
          setMessage(
            "This link is invalid or has expired. Request a new one below."
          );
          setTokenState("invalid");
        } else {
          setMessage("Could not set password. Please try again.");
        }
      });
  };

  return (
    <div className={styles.container}>
      <div className={styles.login}>
        <div>
          <h1>Set Your Password</h1>
        </div>
        {tokenState === "checking" ? (
          <Typography>Validating your link…</Typography>
        ) : tokenState === "invalid" ? (
          <Stack spacing={3}>
            <Typography color="error">
              {message ||
                "This link is invalid or has expired."}
            </Typography>
            <ThemeProvider theme={theme}>
              <Button
                variant="contained"
                color="secondary"
                onClick={requestNewLink}
              >
                Request a new link
              </Button>
            </ThemeProvider>
          </Stack>
        ) : done ? (
          <Typography color="secondary">
            Password set successfully. Redirecting to login…
          </Typography>
        ) : (
          <Stack spacing={3}>
            <TextField
              label="New Password"
              type="password"
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              label="Confirm Password"
              type="password"
              autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value)}
            />
            <Typography variant="body1" color="error">
              {message}
            </Typography>
            <ThemeProvider theme={theme}>
              <Button
                variant="contained"
                color="secondary"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? "Saving…" : "Set Password"}
              </Button>
            </ThemeProvider>
          </Stack>
        )}
      </div>
    </div>
  );
};

export default SetPassword;
